import type { Decimal } from '@/types/course'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

// PUT /gamified-courses/{id} игнорирует null (update_in_database пропускает None), поэтому
// «стереть» значение можно только пустой строкой / нулём / пустым списком.
// См. docs/backend/gamified-course-update-fields.md.
export const clearable = (value: string | null | undefined): string => value ?? ''

export const clearableNumber = (value: number | null | undefined): number => value ?? 0

// Цены в ответе бэка — Decimal = number | string ('0', '9.99'). Сравнивать их с порогами
// напрямую нельзя: '0' === 0 → false, '0' >= 0.1 → false, и бесплатный курс показывал бы
// ошибку порога. Единственная точка приведения к числу — и для валидации, и для отправки.
export const toNumber = (value: Decimal | null | undefined): number => {
  if (value === null || value === undefined) return 0
  const numeric = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(numeric) ? numeric : 0
}

// Пороги платёжных провайдеров (docs/backend/gamified-course-update-fields.md):
// либо ровно 0 (бесплатно), либо не ниже минимума провайдера.
export const PRICE_RUB_MIN = 10
export const PRICE_USD_MIN = 0.1

// "10" остаётся целым, "0.1" печатается как "0.10" — ровно как в контракте бэка.
const formatPriceMin = (min: number): string =>
  Number.isInteger(min) ? String(min) : min.toFixed(2)

/** Порог платёжного провайдера. `zeroAllowed` — про режим продажи, а не про поле:
 *  «Бесплатный» ставит обе цены в 0 (`salesModelPatch`), и общий порог заставил бы
 *  этот режим ругаться на самого себя. В платных режимах ноль больше не валиден —
 *  заказчик просил минимум 10 ₽ / 0.1 $. */
export const priceThresholdError = (
  value: number,
  min: number,
  zeroAllowed: boolean,
): string | null => {
  if (value >= min) return null
  if (zeroAllowed && value === 0) return null
  return zeroAllowed
    ? messagesNow(messages).priceZeroOrMin(formatPriceMin(min))
    : messagesNow(messages).priceMin(formatPriceMin(min))
}

// basePrice — единственная цена без порога провайдера: контракт требует только ge=0.
// Отрицательное значение бэк отклоняет целиком, вместе со всем патчем.
export const priceFloorError = (value: number): string | null =>
  value < 0 ? messagesNow(messages).priceNegative : null
